/**
 * Página de renovación de membresía (Sprint 25-3, mockup M2 ronda 2.1).
 *
 * A la que `TenantPausado` manda con su CTA "Renovar la membresía" cuando
 * el tenant está pausado por falta de renovación — pero también sirve
 * para un admin que quiere renovar por anticipado con el tenant activo.
 * Por eso vive DENTRO de `ProtectedRoute` (requiere sesión) pero FUERA de
 * `OnboardingGate` (ver `App.tsx`): si estuviera adentro, el gate
 * redirigiría a un tenant pausado de vuelta a `TenantPausado` antes de
 * dejarlo llegar acá — el mismo motivo por el que `/onboarding` está
 * afuera.
 *
 * El pago es SIMULADO (`POST /membresia/renovar`, Sprint 25) — "Pagar"
 * marca el pago como exitoso y dispara la reactivación real en el
 * backend. Solo `admin_municipal` puede llamarlo; el backend lo exige
 * con `require_admin_municipal` (403 si no) — acá deshabilitamos el CTA
 * para el resto de roles como defensa en profundidad / mejor UX, no
 * como el control de seguridad real.
 *
 * Solo `trimestral` usa un precio PROVISIONAL (ver
 * `lib/membresia-precios.ts` — decisión pendiente #38, Sprint 26);
 * `semestral` y `annual` son los del business model. De ahí el badge
 * "provisional" junto al precio cuando el período trimestral está
 * seleccionado, y solo entonces.
 *
 * **Con el período vigente (issue #734)** el pago no reinicia nada desde
 * hoy. `planDeRenovacion` (`lib/membresia-vigencia.ts`) aplica las mismas
 * reglas que el backend y el resumen muestra lo que va a pasar antes de
 * pagar. Renovar el mismo plan suma el período nuevo desde el día
 * siguiente al vencimiento. Pasar de Esencial a Pro aplica hoy, conserva
 * el vencimiento y el período pagado (el selector se bloquea), y cobra
 * solo la diferencia por los días que quedan. De Pro a Esencial no se
 * paga: queda para cuando venza.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { LOGIN_ACCESO_RETIRADO } from "@/lib/acceso-retirado";
import {
  fetchEquipo,
  quitarMiembro,
  renovarMembresia,
  type EquipoMiembroDTO,
  type OnboardingContextDTO,
} from "@/lib/api";
import { track } from "@/lib/analytics";
import {
  cobroCambioAPro,
  formatoCOP,
  MEMBRESIA_PRECIOS,
  TRIMESTRAL_ES_MOCK,
  type MembresiaPeriod,
  type MembresiaTier,
} from "@/lib/membresia-precios";
import {
  fechaDesdeIso,
  formatoFecha,
  MESES_POR_PERIODO,
  planDeRenovacion,
  type PlanDeRenovacion,
} from "@/lib/membresia-vigencia";
import { useMunicipality } from "@/lib/municipality";

const SOPORTE_MAILTO = "mailto:soporte@kaptar.com.co";

/** Orden y etiqueta del segmented; la duración sale de `MESES_POR_PERIODO`. */
const PERIODOS: { value: MembresiaPeriod; label: string }[] = [
  { value: "trimestral", label: "Trimestral" },
  { value: "semestral", label: "Semestral" },
  { value: "annual", label: "Anual" },
];

const TIER_LABELS: Record<MembresiaTier, string> = {
  esencial: "Esencial",
  pro: "Pro",
};

const PRO_DIFERENCIAL = "Asistente IA en expedientes · hasta 8 usuarios · expedientes ilimitados";

type CuposPorPlan = NonNullable<OnboardingContextDTO["cupos_por_plan"]>;

/**
 * Lo que cubre un plan, con los números del backend (issue #878). Sin
 * `cupos` (backend sin #912) Pro conserva su texto de siempre y Esencial
 * no dice nada, como antes.
 */
function coberturaDe(tier: MembresiaTier, cupos: CuposPorPlan | undefined): string | null {
  if (!cupos) return tier === "pro" ? PRO_DIFERENCIAL : null;
  const { users, active_projects } = cupos[tier];
  const expedientes =
    active_projects === null ? "expedientes ilimitados" : `${active_projects} expedientes activos`;
  const base = `hasta ${users} personas · ${expedientes}`;
  return tier === "pro" ? `Asistente IA en expedientes · ${base}` : base;
}

const ERROR_GENERICO = "No pudimos procesar el pago. Inténtalo de nuevo.";

function esTierValido(tier: string | null | undefined): tier is MembresiaTier {
  return tier === "esencial" || tier === "pro";
}

/**
 * Tier sugerido por quien nos mandó acá (fix final F8): los upsells de
 * `/membresia` ("Conoce Pro", "Pasa a Pro") navegan con
 * `state: { tier: "pro" }` para que la página abra con Pro seleccionado
 * -- sin eso, "Conoce Pro" aterrizaba en un formulario con Esencial
 * marcado (el plan actual). `location.state` es `unknown` (lo arma
 * quien navega, no el backend), así que se valida antes de usarlo.
 */
function tierDesdeState(state: unknown): MembresiaTier | null {
  if (typeof state !== "object" || state === null) return null;
  const tier = (state as { tier?: unknown }).tier;
  return typeof tier === "string" && esTierValido(tier) ? tier : null;
}

// --- Badges: icono + texto, mismo patrón que el `Badge` de TenantPausado.tsx
// (icon obligatorio, SVG inline) -- acá van sueltos como pills inline en
// vez del componente compartido porque no envuelven en el mismo bloque
// centrado: uno vive junto al precio, el otro dentro de un tierbox.

/** Reloj chico -- badge "provisional" (precio trimestral sin definir aún). */
function ClockIcon() {
  return (
    <svg
      className="h-2.5 w-2.5 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 2" />
    </svg>
  );
}

/** Check chico -- badge "tu plan actual". */
function CheckIcon() {
  return (
    <svg
      className="h-2.5 w-2.5 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M20 6L9 17l-5-5" />
    </svg>
  );
}

// La vigencia nueva (con el overflow de mes clampeado -- Fix 2 MEDIUM de la
// Task 9) y qué cubre cada pago (issue #734) viven en
// `lib/membresia-vigencia.ts`: esta página solo pinta `planDeRenovacion`.

/** % que ahorra pagar anual de una vez vs. pagar semestral dos veces, para un tier dado. */
function ahorroAnualVsSemestral(tier: MembresiaTier): number {
  const semestralAnualizado = MEMBRESIA_PRECIOS[tier].semestral * 2;
  const anual = MEMBRESIA_PRECIOS[tier].annual;
  return Math.round((1 - anual / semestralAnualizado) * 100);
}

/** Fila "Vigencia" del resumen: el período que cubre el pago, o el vencimiento que conserva. */
function vigenciaDelResumen(plan: PlanDeRenovacion): string | null {
  switch (plan.tipo) {
    case "activacion":
    case "renovacion":
      return `${formatoFecha(plan.desde)} → ${formatoFecha(plan.hasta)}`;
    case "cambio_a_pro":
      return `Hasta el ${formatoFecha(plan.vigenteHasta)}, sin cambios`;
    case "cambio_a_esencial_bloqueado":
      return null;
  }
}

/** Lo que el resumen explica bajo sus filas, según el caso (issue #734). */
function notaDelResumen(plan: PlanDeRenovacion): string | null {
  switch (plan.tipo) {
    case "activacion":
      return null;
    case "renovacion":
      return "El período nuevo empieza cuando termina el que ya pagaste: no pierdes días.";
    case "cambio_a_pro": {
      const dias =
        plan.diasRestantes === 1 ? "el día que queda" : `los ${plan.diasRestantes} días que quedan`;
      const periodo = plan.periodoPagado
        ? ` ${PERIODOS.find((p) => p.value === plan.periodoPagado)?.label.toLowerCase()}`
        : "";
      return `Pagas solo la diferencia entre Pro y Esencial por ${dias} de tu período${periodo}.`;
    }
    case "cambio_a_esencial_bloqueado":
      return `Tu plan Pro está pagado hasta el ${formatoFecha(plan.vigenteHasta)}. Podrás pasar a Esencial cuando venza.`;
  }
}

export function RenovarMembresia() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    name,
    displayName,
    tenantStatus,
    tier: tierActual,
    tierPeriod: periodoActual,
    tierValidUntil,
    memberRole,
    isLoading,
    isStaff,
    needsOnboarding,
    context,
    refetch,
  } = useMunicipality();

  // Período: "anual" siempre por default (mockup M2 + Ruling del brief),
  // sin importar cuál sea el período actual del tenant.
  const [period, setPeriod] = useState<MembresiaPeriod>("annual");
  // Tier: default el plan actual del tenant. `tierOverride` en vez de
  // seedear el useState directo con `tierActual` — ese valor todavía es
  // null en el primer render (el contexto está cargando), y el
  // initializer de useState solo corre una vez, así que quedaría pegado
  // en el fallback "esencial" aunque el contexto llegue después con
  // "pro". Con el override, el valor efectivo se deriva en cada render.
  // Arranca con el tier sugerido por `location.state` si viene (F8) --
  // el usuario lo puede cambiar igual que siempre.
  const [tierOverride, setTierOverride] = useState<MembresiaTier | null>(() =>
    tierDesdeState(location.state),
  );
  const tier = tierOverride ?? (esTierValido(tierActual) ? tierActual : "esencial");

  // Telemetría -- 1 fire por mount (deps vacías: cambiar tier/período
  // no debe re-disparar "viewed", solo pasa por acá una vez).
  useEffect(() => {
    track({ event_name: "membresia_renovar_viewed" });
  }, []);

  const mutation = useMutation({
    mutationFn: renovarMembresia,
    onSuccess: async (_data, variables) => {
      // `variables` (lo que se le pasó a `mutate()`) en vez de los
      // `tier`/`period` del closure -- son el plan que efectivamente
      // se cobró, sin depender de que el usuario no haya tocado los
      // selectores mientras la mutation estaba en vuelo.
      track({
        event_name: "membresia_renovar_pagado",
        properties: { tier: variables.tier, period: variables.period },
      });
      // El POST ya reactivó el tenant: pase lo que pase con el refresh
      // del contexto, hay que salir de la página de pago. Si `refetch()`
      // rechaza (red caída justo después de pagar), quedarse acá sin
      // navegar ni avisar -- con el tenant ya activo y el botón de pagar
      // a la vista -- invita a un segundo pago (fix final F7). `/radar`
      // pasa por OnboardingGate, que vuelve a pedir el contexto.
      try {
        await refetch();
      } finally {
        navigate("/radar");
      }
    },
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface">
        <p className="text-sm text-slate-500">Cargando…</p>
      </div>
    );
  }

  // Issue #620: estar fuera de `OnboardingGate` (para que un tenant
  // pausado pueda llegar) dejaba pasar tambien a quien NO tiene tenant
  // -- usuario nuevo o retirado -- y le pintaba la renovacion de "tu
  // municipio" con plan Esencial y "Solo el administrador puede renovar".
  // Sin municipio no hay nada que renovar: mismos destinos que el gate
  // para esos perfiles. Solo despues de que el contexto cargo (arriba se
  // corta con `isLoading`); `context` null tras cargar es error de red,
  // no "sin tenant".
  if (context?.access_revoked) {
    return <Navigate to={LOGIN_ACCESO_RETIRADO} replace />;
  }
  if (context && !context.has_tenant) {
    return <Navigate to={isStaff ? "/admin" : "/onboarding"} replace />;
  }
  // Issue #838: los otros dos destinos del gate. Pagar es contratar, y
  // quien no termino el wizard o no acepto la version vigente de los
  // terminos no ha aceptado el contrato. Al aceptar, ambas pantallas
  // llevan a /radar; si el tenant sigue en pausa, el gate ofrece renovar
  // otra vez, asi que no queda un callejon sin salida.
  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }
  if (context?.legal_pending?.length) {
    return <Navigate to="/actualizacion-legal" replace />;
  }

  const muni = name || displayName || "tu municipio";
  const puedeRenovar = memberRole === "admin_municipal";
  const plan = planDeRenovacion({
    hoy: new Date(),
    tierActual,
    periodoActual,
    vigenteHasta: tierValidUntil ? fechaDesdeIso(tierValidUntil) : null,
    tier,
    period,
  });
  const cambioAPro = plan.tipo === "cambio_a_pro" ? plan : null;
  const bloqueado = plan.tipo === "cambio_a_esencial_bloqueado";
  // Pasar a Pro con el período vigente mantiene el período pagado: el
  // selector lo muestra y no se puede cambiar (el backend lo conserva igual).
  const periodoMostrado: MembresiaPeriod = cambioAPro
    ? (cambioAPro.periodoPagado ?? "annual")
    : period;
  const precio = cambioAPro
    ? cobroCambioAPro(cambioAPro)
    : MEMBRESIA_PRECIOS[tier][periodoMostrado];
  const meses = MESES_POR_PERIODO[periodoMostrado];
  const mostrarProvisional = periodoMostrado === "trimestral" && TRIMESTRAL_ES_MOCK;
  const nota = notaDelResumen(plan);
  const planActualLabel = esTierValido(tierActual) ? TIER_LABELS[tierActual] : null;
  // Issue #736: un tenant activo llega acá desde los upsells de
  // `/membresia` sin haber perdido nada -- el copy de "reactivar el
  // acceso" es solo para el pausado. Y sin `tier_period` (trial, nunca
  // pagó) no hay período que mantener.
  const tenantActivo = tenantStatus === "active";

  // Issue #878: el equipo o los expedientes contra lo que cubre el plan
  // elegido. Del contexto, no de /billing/my-plan: ese endpoint rechaza al
  // tenant pausado, que es justo quien llega a renovar.
  const cupos = context?.cupos_por_plan;
  const seatsUsed = context?.seats_used ?? null;
  const expedientesActivos = context?.active_projects_used ?? null;
  const cuposDelElegido = cupos?.[tier];
  const sobranPersonas =
    cuposDelElegido && seatsUsed !== null ? Math.max(0, seatsUsed - cuposDelElegido.users) : 0;
  const equipoNoCabe = sobranPersonas > 0;
  const limiteExpedientes = cuposDelElegido?.active_projects ?? null;
  const expedientesNoCaben =
    limiteExpedientes !== null &&
    expedientesActivos !== null &&
    expedientesActivos > limiteExpedientes;

  const ahorro = ahorroAnualVsSemestral(tier);
  const hintAnual =
    periodoActual === "annual"
      ? `Anual ahorra ${ahorro}% frente a semestral — es el que usa ${muni} hoy.`
      : `Anual ahorra ${ahorro}% frente a semestral.`;

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-4 py-10">
      <div className="w-full max-w-xl">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs sm:p-8">
          <h1 className="mb-2 text-center text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
            Renovar la membresía de {muni}
          </h1>
          <p className="mx-auto max-w-md text-center text-sm leading-relaxed text-slate-600">
            {periodoActual
              ? "Mantienes tu plan y período, o los cambias aquí mismo — todo en un paso."
              : "Elige el plan y el período aquí mismo — todo en un paso."}
          </p>
          {/* Issue #734: con el período vigente, la página dice hasta cuándo
              está pagado antes de que se elija nada. */}
          {plan.tipo !== "activacion" && planActualLabel && (
            <p
              data-testid="vigente-hasta"
              className="mx-auto mt-1 max-w-md text-center text-sm leading-relaxed font-semibold text-slate-700"
            >
              Tu plan {planActualLabel} está pagado hasta el {formatoFecha(plan.vigenteHasta)}.
            </p>
          )}

          {/* Segmented: período */}
          <div className="mt-6 mb-1 flex justify-center">
            <div
              role="group"
              aria-label="Período de la membresía"
              className="inline-flex overflow-hidden rounded-lg border border-slate-200"
            >
              {PERIODOS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  data-testid={`periodo-${p.value}`}
                  aria-pressed={periodoMostrado === p.value}
                  onClick={() => setPeriod(p.value)}
                  disabled={cambioAPro !== null}
                  className={
                    "px-3.5 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed " +
                    (periodoMostrado === p.value
                      ? "bg-cta-600 text-white disabled:opacity-70"
                      : "text-slate-600 hover:bg-slate-50 disabled:text-slate-300 disabled:hover:bg-transparent")
                  }
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <p className="mb-4 min-h-[1em] text-center text-[11px] text-slate-400">
            {cambioAPro
              ? "Pasar a Pro mantiene el período y el vencimiento que ya pagaste."
              : periodoMostrado === "annual"
                ? hintAnual
                : ""}
          </p>

          {/* Tierboxes */}
          <div className="mb-5 grid grid-cols-2 gap-3">
            {(["esencial", "pro"] as const).map((t) => {
              const selected = tier === t;
              // Si tierActual === t (un literal "esencial"/"pro"), tierActual
              // ya es válido por construcción — no hace falta esTierValido acá.
              const esPlanActual = tierActual === t;
              return (
                <button
                  key={t}
                  type="button"
                  data-testid={`tier-${t}`}
                  aria-pressed={selected}
                  onClick={() => setTierOverride(t)}
                  className={
                    "flex-1 rounded-xl border p-4 text-left transition-colors " +
                    (selected
                      ? "border-2 border-cta-500 bg-cta-50"
                      : "border-slate-200 bg-white hover:border-slate-300")
                  }
                >
                  <div className="text-sm font-bold text-slate-900">{TIER_LABELS[t]}</div>
                  <div className="mt-1 flex flex-wrap items-baseline gap-x-1.5 gap-y-1">
                    <span className="text-xl font-extrabold text-slate-900 tabular-nums">
                      {formatoCOP(MEMBRESIA_PRECIOS[t][periodoMostrado])}
                    </span>
                    <span className="text-xs text-slate-500">/ {meses} meses</span>
                    {mostrarProvisional && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-amber-700 uppercase">
                        <ClockIcon />
                        provisional
                      </span>
                    )}
                  </div>
                  {esPlanActual && (
                    <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-cta-100 px-2 py-0.5 text-[11px] font-bold text-cta-600">
                      <CheckIcon />
                      tu plan actual
                    </span>
                  )}
                  {coberturaDe(t, cupos) && (
                    <p className="mt-1.5 text-xs leading-relaxed text-slate-600">
                      {coberturaDe(t, cupos)}
                    </p>
                  )}
                </button>
              );
            })}
          </div>

          {/* Resumen */}
          <div className="mb-5 rounded-xl border border-slate-200 bg-white p-4">
            <h3 className="mb-3 text-xs font-semibold tracking-wide text-slate-400 uppercase">
              Resumen
            </h3>
            <div className="flex justify-between gap-4 py-1 text-sm">
              <span className="text-slate-600">Plan seleccionado</span>
              <span data-testid="resumen-plan" className="text-right font-semibold text-slate-900">
                {cambioAPro
                  ? "Pro · desde hoy"
                  : `${TIER_LABELS[tier]} · ${PERIODOS.find((p) => p.value === periodoMostrado)?.label.toLowerCase()}`}
              </span>
            </div>
            {!bloqueado && (
              <>
                <div className="flex justify-between gap-4 py-1 text-sm">
                  <span className="text-slate-600">
                    {cambioAPro ? "Vigencia" : "Vigencia nueva"}
                  </span>
                  <span
                    data-testid="resumen-vigencia"
                    className="text-right font-semibold text-slate-900"
                  >
                    {vigenciaDelResumen(plan)}
                  </span>
                </div>
                <div className="flex justify-between gap-4 py-1 text-sm">
                  <span className="text-slate-600">Total</span>
                  <span
                    data-testid="resumen-total"
                    className="text-right font-semibold text-slate-900"
                  >
                    {formatoCOP(precio)}
                  </span>
                </div>
              </>
            )}
            {nota && (
              <p
                data-testid="resumen-nota"
                className={
                  "mt-2 text-xs leading-relaxed " +
                  (bloqueado ? "text-amber-800" : "text-slate-500")
                }
              >
                {nota}
              </p>
            )}
          </div>

          {equipoNoCabe && cuposDelElegido && seatsUsed !== null && (
            <EquipoNoCabe
              plan={TIER_LABELS[tier]}
              seatsUsed={seatsUsed}
              cupo={cuposDelElegido.users}
              sobran={sobranPersonas}
              puedeQuitar={puedeRenovar}
              onRenovarEnPro={() => setTierOverride("pro")}
              onQuitado={() => void refetch()}
            />
          )}
          {expedientesNoCaben && limiteExpedientes !== null && expedientesActivos !== null && (
            <p
              data-testid="expedientes-no-caben"
              className="mb-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm leading-relaxed text-slate-700"
            >
              Tienes {expedientesActivos} expedientes activos y {TIER_LABELS[tier]} cubre{" "}
              {limiteExpedientes}: podrás seguir trabajándolos, pero no abrir otros hasta tener
              menos de {limiteExpedientes}.
            </p>
          )}

          {mutation.isError && (
            <div
              role="alert"
              className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
            >
              {mutation.error instanceof Error ? mutation.error.message : ERROR_GENERICO}
            </div>
          )}

          <button
            type="button"
            onClick={() => mutation.mutate({ tier, period: periodoMostrado })}
            disabled={!puedeRenovar || mutation.isPending || bloqueado || equipoNoCabe}
            className="block w-full rounded-md bg-cta-600 px-4 py-2.5 text-center text-sm font-semibold text-white transition-colors hover:bg-cta-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {!tenantActivo
              ? "Pagar y reactivar el acceso"
              : cambioAPro
                ? "Pagar la diferencia y pasar a Pro"
                : "Pagar la membresía"}
          </button>
          {!puedeRenovar && (
            <p className="mt-2 text-center text-xs text-amber-700">
              Solo el administrador de la membresía puede renovar
            </p>
          )}

          <a
            href={SOPORTE_MAILTO}
            className="mt-2 block w-full rounded-md border border-slate-300 px-4 py-2.5 text-center text-sm font-semibold text-brand-700 transition-colors hover:bg-brand-50"
          >
            Contactar soporte
          </a>

          <p className="mt-3 text-center text-xs text-slate-500">
            {!tenantActivo && (
              <>
                Al confirmar el pago, el acceso de todo el equipo vuelve de inmediato.
                <br />
              </>
            )}
            ¿Necesitas cuenta de cobro u otro documento para tu contratación? También por soporte.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Aviso de equipo de más (issue #878): no se paga un plan en el que el
 * equipo no cabe. El admin quita a quien sobra aquí mismo -- un municipio
 * pausado no llega a /membresia, y la API le deja quitar igual (F14:
 * quitar libera cupo, no agrega acceso) -- o elige Pro.
 */
function EquipoNoCabe({
  plan,
  seatsUsed,
  cupo,
  sobran,
  puedeQuitar,
  onRenovarEnPro,
  onQuitado,
}: {
  plan: string;
  seatsUsed: number;
  cupo: number;
  sobran: number;
  puedeQuitar: boolean;
  onRenovarEnPro: () => void;
  onQuitado: () => void;
}) {
  const queryClient = useQueryClient();
  const equipo = useQuery({
    queryKey: ["membresia-equipo"],
    queryFn: fetchEquipo,
    enabled: puedeQuitar,
  });
  const [confirmando, setConfirmando] = useState<string | null>(null);
  const quitar = useMutation({
    mutationFn: (miembro: EquipoMiembroDTO) => quitarMiembro(miembro.membership_id),
    onSuccess: () => {
      track({ event_name: "equipo_quitar_confirmado" });
      setConfirmando(null);
      void queryClient.invalidateQueries({ queryKey: ["membresia-equipo"] });
      onQuitado();
    },
  });

  return (
    <div
      data-testid="equipo-no-cabe"
      role="alert"
      className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
    >
      <p className="font-semibold">
        Tu equipo tiene {seatsUsed} personas y {plan} cubre {cupo}.
      </p>
      <p className="mt-0.5">
        {puedeQuitar
          ? `Quita a ${sobran} o renueva en Pro.`
          : `El administrador de la membresía tiene que quitar a ${sobran}, o renovar en Pro.`}
      </p>

      {puedeQuitar && equipo.isLoading && (
        <div className="mt-3 space-y-2" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-5 animate-pulse rounded bg-amber-100" />
          ))}
        </div>
      )}
      {puedeQuitar && equipo.isError && (
        <p className="mt-2 text-xs">
          No pudimos cargar tu equipo. Recarga la página para intentar de nuevo.
        </p>
      )}
      {puedeQuitar && equipo.data && (
        <ul className="mt-3 divide-y divide-amber-100 rounded-md border border-amber-100 bg-white">
          {equipo.data.miembros.map((m) => {
            const nombre = m.full_name || "Sin nombre";
            const esAdmin = m.role === "admin_municipal";
            return (
              <li
                key={m.membership_id}
                className="flex items-center justify-between gap-2 px-3 py-2"
              >
                <span className="text-slate-800">
                  {nombre}
                  <span className="text-xs text-slate-500">
                    {esAdmin ? " · Administrador" : ""}
                    {m.accepted_at ? "" : " · invitación pendiente"}
                  </span>
                </span>
                {!esAdmin &&
                  (confirmando === m.membership_id ? (
                    <span className="flex shrink-0 gap-2">
                      <button
                        type="button"
                        onClick={() => quitar.mutate(m)}
                        disabled={quitar.isPending}
                        aria-label={`Sí, quitar a ${nombre}`}
                        className="text-xs font-semibold text-red-700 underline disabled:opacity-50"
                      >
                        Sí, quitar
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmando(null)}
                        className="text-xs text-slate-600 underline"
                      >
                        Cancelar
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmando(m.membership_id)}
                      aria-label={`Quitar a ${nombre}`}
                      className="shrink-0 text-xs font-semibold text-brand-700 underline"
                    >
                      Quitar
                    </button>
                  ))}
              </li>
            );
          })}
        </ul>
      )}
      {quitar.isError && (
        <p className="mt-2 text-xs text-red-800">
          {quitar.error instanceof Error
            ? quitar.error.message
            : "No pudimos quitar a esa persona."}
        </p>
      )}

      <button
        type="button"
        onClick={onRenovarEnPro}
        className="mt-3 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100"
      >
        Renovar en Pro
      </button>
    </div>
  );
}
